import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { signalTransitionReady } from '../components/EventTransition';
import './LiveEventPage.css';
import './WouldYouRatherPage.css';

const PARTYKIT_HOST = process.env.REACT_APP_PARTYKIT_HOST || 'thetopia-live.lafless.partykit.dev';

/* ── Electric Border (Intensified Mode) ── */
function ElectricBorder({ color, intensity = 'normal' }) {
  const turbRef = useRef(null);
  const frameRef = useRef(null);
  const filterId = useMemo(() => `elec-${Math.random().toString(36).slice(2, 8)}`, []);

  useEffect(() => {
    let t = 0;
    const speed = intensity === 'intense' ? 0.06 : 0.03;
    const animate = () => {
      t += speed;
      if (turbRef.current) {
        turbRef.current.setAttribute('seed', Math.floor(t * 5) % 200);
      }
      frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [intensity]);

  const strokeW = intensity === 'intense' ? 4 : 2;
  const blur = intensity === 'intense' ? 4 : 2;
  const displacement = intensity === 'intense' ? 15 : 8;

  return (
    <svg
      style={{
        position: 'absolute',
        inset: '-5px',
        width: 'calc(100% + 10px)',
        height: 'calc(100% + 10px)',
        pointerEvents: 'none',
        zIndex: 2,
        overflow: 'visible',
      }}
    >
      <defs>
        <filter id={filterId} x="-20%" y="-20%" width="140%" height="140%" colorInterpolationFilters="sRGB">
          <feTurbulence ref={turbRef} type="fractalNoise" baseFrequency="0.03" numOctaves="4" seed="0" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale={displacement} xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <filter id={`${filterId}-glow`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation={blur} result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g filter={`url(#${filterId}-glow)`}>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="none" stroke={color} strokeWidth={strokeW}
          filter={`url(#${filterId})`}
        />
      </g>
    </svg>
  );
}

/* ── Timer Display ── */
function WyrTimer({ state }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);

  if (state.roundState !== 'active' || !state.roundStartedAt) return null;

  const elapsed = (state.pausedAt ? state.pausedAt : now) - state.roundStartedAt - state.pausedElapsed;
  const remaining = Math.max(0, (state.roundDurationSec * 1000) - elapsed);
  const secs = Math.ceil(remaining / 1000);
  const mins = Math.floor(secs / 60);
  const s = secs % 60;

  return (
    <div className="wyr-timer">
      {mins}:{s.toString().padStart(2, '0')}
    </div>
  );
}

export default function WouldYouRatherViewPage() {
  const { slug } = useParams();
  const sessionToken = process.env.REACT_APP_PARTYKIT_VIEWER_TOKEN || '';

  const [roomState, setRoomState] = useState(null);
  const [introPhase, setIntroPhase] = useState(0);

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  // Intro animation
  useEffect(() => {
    const t1 = setTimeout(() => setIntroPhase(1), 100);
    const t2 = setTimeout(() => setIntroPhase(2), 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // WebSocket connection
  const connect = useCallback(() => {
    if (!slug || !sessionToken) return;

    clearTimeout(reconnectRef.current);
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    const protocol = PARTYKIT_HOST.includes('localhost') ? 'ws' : 'wss';
    const ws = new WebSocket(`${protocol}://${PARTYKIT_HOST}/parties/wyr/${slug}`);
    wsRef.current = ws;

    ws.onopen = () => {
      // Use a unique fingerprint per overlay instance so it doesn't conflict with real viewers
      const overlayId = `overlay-${Math.random().toString(36).slice(2, 10)}`;
      ws.send(JSON.stringify({
        type: 'join',
        token: sessionToken,
        displayName: 'Overlay',
        fingerprint: overlayId,
        viewMode: true,
      }));
      signalTransitionReady();
    };

    ws.onmessage = ({ data }) => {
      try {
        const msg = JSON.parse(data);
        if (msg.type === 'state') {
          setRoomState(msg);
        } else if (msg.type === 'update') {
          setRoomState(prev => prev ? {
            ...prev,
            leftVotes: msg.leftVotes,
            rightVotes: msg.rightVotes,
            leftPct: msg.leftPct,
            rightPct: msg.rightPct,
            connections: msg.connections,
          } : null);
        } else if (msg.type === 'viewers') {
          setRoomState(prev => prev ? { ...prev, connections: msg.connections } : null);
        }
      } catch {}
    };

    ws.onclose = () => {
      wsRef.current = null;
      reconnectRef.current = setTimeout(() => connect(), 2000);
    };

    ws.onerror = () => {};
  }, [slug, sessionToken]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const isActive = roomState?.roundState === 'active';
  const isWaiting = roomState?.roundState === 'waiting';
  const isIdle = !roomState || roomState.roundState === 'idle';
  const isPaused = roomState?.roundState === 'paused';
  const intensified = roomState?.intensified || false;

  // Compute current vote data (always visible, even before any votes)
  const showWaiting = isWaiting && roomState?.lastResult;
  const leftVotes = showWaiting ? roomState.lastResult.leftVotes : (roomState?.leftVotes ?? 0);
  const rightVotes = showWaiting ? roomState.lastResult.rightVotes : (roomState?.rightVotes ?? 0);
  const totalVotes = leftVotes + rightVotes;
  const leftPct = showWaiting
    ? (totalVotes > 0 ? Math.round(leftVotes / totalVotes * 100) : 0)
    : (roomState?.leftPct ?? 0);
  const rightPct = showWaiting
    ? (totalVotes > 0 ? Math.round(rightVotes / totalVotes * 100) : 0)
    : (roomState?.rightPct ?? 0);

  return (
    <section className={`le-page wyr-page${intensified ? ' wyr-intensified' : ''}`} style={{ pointerEvents: 'none' }}>
      <div className="wyr-layout">
        <img src={intensified ? '/img/wyr-question-marks-intensified.png' : '/img/wyr-question-marks.png'} alt="" className={`wyr-question-marks${introPhase >= 2 ? ' wyr-qm-visible' : ''}`} />
        <div className="wyr-logo-wrap">
          <img
            src={intensified ? '/img/wyr-screen-intensified.png' : '/img/wyr-screen.png'}
            alt="Would You Rather?"
            className={`wyr-logo${introPhase === 1 ? ' wyr-logo-intro' : ''}${introPhase >= 2 ? ' wyr-logo-final' : ''}`}
          />
          <img
            src={intensified ? '/img/wyr-screen-intensified.png' : '/img/wyr-screen.png'}
            alt=""
            className="wyr-logo-glow"
          />
        </div>

        <div className={`wyr-content${introPhase >= 2 ? ' wyr-content-visible' : ''}`}>
          {isIdle && <div className="wyr-status-msg">Waiting for the game to start...</div>}
          {isPaused && <div className="wyr-status-msg">Game paused</div>}
          {isWaiting && <div className="wyr-status-msg">Waiting for next question...</div>}

          {isActive && <WyrTimer state={roomState} />}

          <div className="wyr-choices">
            <div className="wyr-choice-wrap">
              <div className="wyr-choice wyr-choice-left">
                {intensified && <ElectricBorder color="#39ff73" intensity="normal" />}
                <span className="wyr-choice-desc">
                  {(intensified && roomState?.question?.leftIntensifiedDesc) || roomState?.question?.leftDesc || (isWaiting && roomState?.lastResult?.leftDesc) || '???'}
                </span>
                <div className="wyr-pct-reveal wyr-pct-visible">
                  <div className="wyr-pct-container">
                    <div className="wyr-pct-bar wyr-pct-bar-left" style={{ width: `${leftPct}%` }} />
                    <span className="wyr-pct-label">{leftPct}% ({leftVotes} {leftVotes === 1 ? 'vote' : 'votes'})</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="wyr-vs">OR</div>

            <div className="wyr-choice-wrap">
              <div className="wyr-choice wyr-choice-right">
                {intensified && <ElectricBorder color="#d946ef" intensity="normal" />}
                <span className="wyr-choice-desc">
                  {(intensified && roomState?.question?.rightIntensifiedDesc) || roomState?.question?.rightDesc || (isWaiting && roomState?.lastResult?.rightDesc) || '???'}
                </span>
                <div className="wyr-pct-reveal wyr-pct-visible">
                  <div className="wyr-pct-container">
                    <div className="wyr-pct-bar wyr-pct-bar-right" style={{ width: `${rightPct}%` }} />
                    <span className="wyr-pct-label">{rightPct}% ({rightVotes} {rightVotes === 1 ? 'vote' : 'votes'})</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="wyr-viewers">
            {roomState?.connections || 0} watching
          </div>
        </div>
      </div>
    </section>
  );
}
